import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { authApi, getToken, getRole, api } from "../api/api";

const AuthContext = createContext(null);

// Token validation interval (5 minutes)
const TOKEN_VALIDATION_INTERVAL = 5 * 60 * 1000;
// Session timeout (30 minutes of inactivity)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const validationIntervalRef = useRef(null);
  const activityCleanupRef = useRef(null);

  const clearAuth = () => {
    localStorage.removeItem("ctf_token");
    localStorage.removeItem("ctf_role");
    localStorage.removeItem("ctf_user");
    setUser(null);
    setRole(null);
    if (validationIntervalRef.current) {
      clearInterval(validationIntervalRef.current);
      validationIntervalRef.current = null;
    }
  };

  const validateToken = async (skipRedirect = false) => {
    const token = getToken();
    const r = getRole();
    if (!token || !r) {
      if (!skipRedirect) {
        clearAuth();
      }
      return false;
    }

    try {
      // Validate token with backend by calling appropriate endpoint
      let validationEndpoint;
      if (r === "admin") {
        validationEndpoint = "/admin/profile";
      } else if (r === "member") {
        validationEndpoint = "/member/profile";
      } else if (r === "student") {
        // High security: Use /user endpoint for token validation
        validationEndpoint = "/student/user";
      } else {
        return false;
      }
      
      const validationResponse = await api(validationEndpoint);
      if (r === "student" && validationResponse?.data) {
        const latestStudent = validationResponse.data;
        const mergedStudent = { ...(user || {}), ...latestStudent };
        setUser(mergedStudent);
        localStorage.setItem("ctf_user", JSON.stringify(mergedStudent));
      }
      
      // Update last activity
      lastActivityRef.current = Date.now();
      return true;
    } catch (error) {
      // Token validation failed - but don't clear auth immediately
      // If user is actively logged in (has user state), trust it and don't redirect
      // This prevents redirect loops when backend validation temporarily fails
      console.warn("Token validation failed:", error.message);
      
      // Only clear auth and redirect if skipRedirect is false AND user state is not set
      // This means it's a fresh page load, not an active session
      if (!skipRedirect && !user) {
        clearAuth();
        const path = window.location.pathname;
        if (path.startsWith("/admin") || path.startsWith("/member") || path.startsWith("/student")) {
          const loginPath = r === "admin" ? "/admin/login" : r === "member" ? "/member/login" : "/student/login";
          window.location.href = loginPath;
        }
      }
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();
      const r = getRole();
      
      if (token && r) {
        try {
          const userData = localStorage.getItem("ctf_user");
          if (userData) {
            // Set user and role first (trust localStorage)
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            setRole(r);
            lastActivityRef.current = Date.now();
            
            // Validate token in background (non-blocking, skip redirect)
            // Don't clear auth if validation fails - trust the stored user data
            validateToken(true).catch(() => {
              // Validation failed but don't clear auth - user might be logged in
              console.warn("Token validation failed on mount, but keeping user logged in");
            });
            
            // Set up periodic token validation (skip redirect on periodic checks)
            // Only clear auth if user is truly logged out, not on temporary validation failures
            validationIntervalRef.current = setInterval(() => {
              validateToken(true); // Skip redirect on periodic validation
            }, TOKEN_VALIDATION_INTERVAL);
            
            // Set up session timeout check
            setInterval(() => {
              const now = Date.now();
              if (now - lastActivityRef.current > SESSION_TIMEOUT) {
                clearAuth();
                const loginPath = r === "admin" ? "/admin/login" : r === "member" ? "/member/login" : "/student/login";
                window.location.href = loginPath;
              }
            }, 60 * 1000);
            
            // Track user activity
            const updateActivity = () => { lastActivityRef.current = Date.now(); };
            window.addEventListener("mousedown", updateActivity);
            window.addEventListener("keydown", updateActivity);
            window.addEventListener("scroll", updateActivity);
            window.addEventListener("touchstart", updateActivity);
            activityCleanupRef.current = () => {
              window.removeEventListener("mousedown", updateActivity);
              window.removeEventListener("keydown", updateActivity);
              window.removeEventListener("scroll", updateActivity);
              window.removeEventListener("touchstart", updateActivity);
            };
          } else {
            // No user data in localStorage - clear auth
            clearAuth();
          }
        } catch (e) {
          console.error("Failed to parse user data:", e);
          clearAuth();
        }
      } else {
        // No token or role - clear auth
        clearAuth();
      }
      setLoading(false);
    };

    initializeAuth();
    
    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
      if (activityCleanupRef.current) {
        activityCleanupRef.current();
      }
    };
  }, []);

  const login = async (type, credentials) => {
    let res;
    try {
      if (type === "admin") {
        res = await authApi.adminLogin(credentials.email, credentials.password);
      } else if (type === "member") {
        res = await authApi.memberLogin(credentials.email, credentials.password);
      } else if (type === "student") {
        res = await authApi.studentLogin(credentials.email, credentials.password);
      } else {
        throw new Error("Invalid login type");
      }
    } catch (err) {
      console.error("Login API error:", err);
      throw err;
    }

    const data = res?.data || res;
    const token = data?.token;
    const u = data?.admin || data?.member || data?.student;
    
    if (!token || !u) {
      console.error("Login response missing token or user:", { res, data, token, u });
      throw new Error("Invalid response from server: missing token or user data");
    }
    
    const r = type;
    
    // Store auth data
    localStorage.setItem("ctf_token", token);
    localStorage.setItem("ctf_role", r);
    localStorage.setItem("ctf_user", JSON.stringify(u));
    setUser(u);
    setRole(r);
    lastActivityRef.current = Date.now();
    
    // Set up periodic validation after login (skip redirect on periodic checks)
    if (validationIntervalRef.current) {
      clearInterval(validationIntervalRef.current);
    }
    validationIntervalRef.current = setInterval(() => {
      validateToken(true); // Skip redirect on periodic validation
    }, TOKEN_VALIDATION_INTERVAL);
    
    return res;
  };

  const logout = async () => {
    try {
      if (role === "admin") await authApi.adminLogout();
      else if (role === "member") await authApi.memberLogout();
      else if (role === "student") await authApi.studentLogout();
    } catch (_) {}
    clearAuth();
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
